import {useSelector} from 'react-redux'
import {
  getHotelOfferEntity,
  getIsFiltersApplied
} from 'modules/sapiSearch/selectors'
import {getIsPrimaryOfferCheapest} from 'modules/searchApi/selectors'

/**
 * Filter-aware gate for the mobile Price Insights surfaces (banner above the
 * OfferBox and compact insights card below it).
 *
 * Mirrors the rule previously inlined in `PriceTrendMobile`: hide when a
 * filter has bumped a non-primary offer to cheapest. Redirect (Search API)
 * primary offers are not affected by room filters, so they always pass.
 *
 * Reads the offer entity straight from the sapiSearch slice — no RTK Query
 * subscription — so callers don't pay for a duplicate fetch and tests don't
 * need to mock useGetOffersQuery.
 *
 * Returns `true` while the offer entity hasn't arrived yet so the existing
 * loading skeletons stay visible — the late-arriving filter signal will
 * collapse the surface on the next render if needed.
 */
export const useShouldShowPriceInsightsForFilters = (
  hotelId: string
): boolean => {
  const isFiltersApplied = useSelector(getIsFiltersApplied)
  const isPrimaryOfferCheapest = useSelector(getIsPrimaryOfferCheapest)
  const offerEntity = useSelector(state => getHotelOfferEntity(state, hotelId))

  if (!isFiltersApplied) return true

  const primaryOffer = offerEntity?.offers?.[0]
  if (!primaryOffer) return true

  const isSearchApiOffer = !('prices' in primaryOffer)
  return isSearchApiOffer || isPrimaryOfferCheapest
}
